"use client";
import React, { useState } from "react";
import { USER_LOGIN } from "@/service/login.service";
import { Eye, EyeOff, LockKeyhole, UserRound } from "lucide-react";
import Image from "next/image";
import { toast } from "react-toastify";
import PasswordExpiredModal from "./PasswordExpiredModal";
import dynamic from "next/dynamic";
import PasswordResetModal from "./PasswordResetModal";
import { handleAxiosError } from "@/lib/errorHandler";

const ForgotPasswordModal = dynamic(
  () => import("@/components/modals/ForgotPasswordModal"),
  { ssr: false }
);

const LoginView = ({ onLoginSuccess, onLoginFailure }) => {
  const [userName, setUserName] = useState("");
  const [userNameError, setUserNameError] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordExpiredModal, setPasswordExpiredModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [passwordResetModal, setPasswordResetModal] = useState(false);

  const successToast = () => {
    toast.success("Logged in successfully!");
  };
  
  const handleKeyDown = (e) => {
    const char = e.key;
    const currentValue = e.currentTarget.value;
    const allowedControlKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];
    if (allowedControlKeys.includes(char)) {
      return;
    }
    if (
      char === " " ||
      !/^[a-zA-Z_0-9]$/.test(char) ||
      (char === "_" && currentValue.includes("_")) ||
      (currentValue.length === 0 && char === "_")
    ) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    setUserName(e.target.value.toUpperCase());
  };

  const handleBlur = () => {
    if (userName.endsWith("_")) {
      setUserName(userName.slice(0, -1));
    }
  };

  const handlePasswordKeyDown = (e) => {
    if (error) {
      setError("");
    }
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      const response = await USER_LOGIN({ userName, password });
      if (response.statusCode === 200) {
        successToast();
        // setLoading(true);
        sessionStorage.setItem("access_token", response?.data?.access_token);
        sessionStorage.setItem("expires_in", response?.data?.expires_in);
        sessionStorage.setItem(
          "refresh_expires_in",
          response?.data?.refresh_expires_in
        );
        sessionStorage.setItem("refresh_token", response?.data?.refresh_token);
        sessionStorage.setItem("token_type", response?.data?.token_type);
        sessionStorage.setItem("isLogin", true);
        sessionStorage.setItem("userName", userName);
        setError("");
        onLoginSuccess();
      }
    } catch (error) {
      // setUserName("");
      // setPassword("");
      setLoading(false);
      const errorMsg =
        error?.response?.data?.errorDescription || "Login failed";
      if (
        error?.response?.data?.errorDescription?.toLowerCase() ===
        "password has expired. please change your password"
      ) {
        setPasswordExpiredModal(true);
      }
      if (
        error?.response?.data?.errorDescription?.toLowerCase() ===
        "please complete your first-time password reset before logging in. check your email for credentials."
      ) {
        setPasswordResetModal(true);
      }
      handleAxiosError(error);
      setError(errorMsg);
      onLoginFailure();
    }
  };

  return (
    <>
      <div className="flex flex-col md:flex-row h-screen w-full overflow-hidden">
          <div className="hidden md:flex md:w-1/2 items-center justify-center">
            <Image
              width={500}
              height={500}
              src="/assets/login/frame-844.svg"
              alt="Login Illustration"
              className="w-full  h-full object-cover"
              unoptimized
            />
          </div>
          <div className="flex flex-col w-full h-full justify-center md:w-1/2 items-center text-center py-10 md:py-20 lg:py-24 px-6 sm:px-8 lg:px-12">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-2xl font-bold">
              Login
            </h1>

            <div className="mt-6 w-full max-w-sm">
              <form autoComplete="off">
                <div className="p-2 rounded-lg flex items-center mb-4 border border-gray-300 w-full bg-white focus-within:border-gray-500">
                  <UserRound
                    size={25}
                    color="#1155df"
                    strokeWidth={3}
                    className="w-7 h-7 mr-2"
                  />
                  <input
                    type="text"
                    placeholder="Enter User Name"
                    className="w-full bg-white text-sm lg:text-base outline-none autofill-disable"
                    value={userName}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    id="userName"
                    aria-label="Username"
                  />
                </div>
                {userNameError && (
                  <p className="text-red-500">Please enter username</p>
                )}
                <div className="p-1 rounded-lg flex items-center mb-4 border border-gray-300 w-full bg-white focus-within:border-gray-500 ">
                  <LockKeyhole
                    size={25}
                    color="#1155df"
                    strokeWidth={3}
                    className="w-7 h-7 mr-2 ml-1"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter Password"
                    className="flex-1 p-2 bg-white text-sm lg:text-base outline-none autofill-disable"
                    value={password}
                    onKeyDown={handlePasswordKeyDown}
                    onChange={(e) => setPassword(e.target.value)}
                    id="password"
                    aria-label="Password"
                  />
                  {passwordError && (
                    <p className="text-red-500">Please enter password</p>
                  )}
                  <button
                    type="button"
                    className="p-1"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? "Show Password" : "Hide Password"
                    }
                  >
                    {showPassword ? <Eye /> : <EyeOff />}
                  </button>
                </div>
                <div className="items-end text-right">
                  <a
                    // href="#"
                    // type="button"
                    className="text-sm text-[#2787DC] mb-4 block hover:text-blue-800 hover:underline hover:cursor-pointer"
                    onClick={() => setShowForgotPasswordModal(true)}
                  >
                    Forgot password?
                  </a>
                </div>
              </form>
              <button
                disabled={loading || !userName || !password}
                className={`w-full py-3 text-white rounded-full text-lg lg:text-base 
                  ${
                    loading || !userName || !password
                      ? "bg-[#2787DC] opacity-50 cursor-not-allowed"
                      : "bg-[#2787DC] hover:bg-blue-800 hover:cursor-pointer"
                  }`}
                onClick={handleLogin}
                id="login"
                aria-label="Login"
              >
                {loading ? "Please wait..." : "Login"}
              </button>
            </div>
          </div>
          <div className="hidden md:flex flex-col justify-end">
            <Image
              width={150}
              height={150}
              src="/assets/login/ellipse-32.svg"
              alt="Login Illustration"
              unoptimized
            />
          </div>
        </div>
      {passwordExpiredModal && (
        <PasswordExpiredModal
          isOpen={passwordExpiredModal}
          onClose={() => setPasswordExpiredModal(false)}
          userName={userName?.toUpperCase()}
          password={password}
          type="expired"
        />
      )}
      {passwordResetModal && (
        <PasswordResetModal
          isOpen={passwordResetModal}
          onClose={() => setPasswordResetModal(false)}
          userName={userName?.toUpperCase()}
          password={password}
        />
      )}
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        setIsOpen={setShowForgotPasswordModal}
        type="forgot"
      />
    </>
  );
};

export default LoginView;
